import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import nookies from 'nookies';
import marvelEmitter from '@marvelapp/react-ab-test/lib/emitter';
import { Experiment as MarvelExperiment, Variant as MarvelVariant } from '@marvelapp/react-ab-test';
import env from '/env';
import { apolloClient as client } from '/lib/initApollo';
import { getCurrentBreakpoint, getOnlyMobile, isGeneratorMobileView } from '/styles/mixins';
import { IntlProvider } from '/imports/core/api/useIntl';
import { ResponsiveProvider } from '/imports/core/api/responsiveContext';
import { useRouter } from 'next/router';
import {
  getLocaleFromPath,
  getDefaultLanguage,
  getAvailableLanguages,
  isJobTrackEnable,
  getDeviceInformation,
  RESUMEDONE_BLOGS,
  removeExperiment,
} from '/lib/helpers';
import { LANGUAGES_CODES, supported } from '/imports/generator/api/constants';
import { MixpanelProvider } from '/imports/core/api/useMixpanel';
import GlobalStateProvider from 'imports/core/api/global.context';
import { CURRENT_USER } from '/imports/core/api/apollo/client/queries';
import QueryClientWrapper from 'lib/QueryClient';
import dynamic from 'next/dynamic';
const PageRender = dynamic(() => import('/components/PageRender'));
const isDev = env.NODE_ENV === 'development';
const isProd = env.NODE_ENV === 'production';

const EXPERIMENT_LIST = [
  'budapest_resume_experiment',
  'exp_tooltip_preview',
  'chicago_resume_experiment',
  'rotterdam_resume_experiment',
  'perth_resume_experiment',
  'riga_resume_experiment',
  'prague_resume_experiment',
  'rotterdamv3_experiment',
  'custom_fonts_v2_experiment',
  'resume_category_exp',
  'resume_option_heading_exp',
  'resume_linkedin_exp',
  'resume_linkedin_exp_v2',
  'resume_linkedin_search',
  'exp_jobtrack_exit_hack',
  'resume_upload_modal',
  'exp_gift_popup',
  'exp_skill_section',
  'exp_split_skill_upload',
  'exp_skill_system',
  'click_to_edit_exp',
  'click_to_edit_exp_new',
  'exp_resume_bullets',
  'click_to_edit_exp_new_v4',
  'exp_resume_bullets_v2',
  'click_to_edit_exp_new_v5',
  'click_to_edit_background_exp',
  'click_to_edit_exp_new_v6',
  'click_to_edit_funnel_exp',
  'click_to_edit_exp_new_v7',
  'swap_rtl_columns_exp',
  'arabic_new_font_exp',
  'exp_writing_feature_pricing',
  'exp_review_presentation',
  'exp_resumes_writing',
  'exp_block_repositing',
  'exp_resume_parsing_model',
];

const JOB_TRACK_DOMAINS = ['cvdeboss', 'modeles-cv'];

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall >= delay) {
      lastCall = now;
      fn.apply(this, args);
    }
  };
}

const getScreenResolution = () => {
  if (typeof window === 'undefined') return [undefined, undefined];
  const screenWidth = window.screen.width * window.devicePixelRatio;
  const screenHeight = window.screen.height * window.devicePixelRatio;
  return [screenWidth, screenHeight];
};

const customizePushtellDebugger = () => {
  if (typeof window === 'undefined') return;
  const devToolModal = document.querySelector('#pushtell-debugger');
  if (devToolModal) {
    devToolModal.style.maxHeight = '90vh';
    devToolModal.style.overflow = 'auto';
  }
};

const RenderJobTrack = ({ children, host }) => {
  const router = useRouter();
  const isHostInDomain = useMemo(() => JOB_TRACK_DOMAINS.includes(host), [host]);

  useEffect(() => {
    if (!isHostInDomain) {
      removeExperiment('job-tracking');
    }
  }, []);

  useEffect(() => {
    if (!isHostInDomain && isProd) return;

    const checkJobtrackAccess = async () => {
      const { asPath = '' } = router || {};
      if (asPath.includes('/user-resume')) return;

      try {
        const { agent_client_id } = nookies.get({});
        const { data } = await client.query({
          query: CURRENT_USER,
          fetchPolicy: 'network-only',
        });

        const currentUser = data?.useInfo;
        const isAgent = currentUser?.role === 'agent' || !!agent_client_id;
        const isExtensionUser = !!currentUser?.extensionOnboardOption && currentUser?.extensionOnboardOption !== 'skip';

        if (!(isAgent || isExtensionUser)) {
          marvelEmitter.setActiveVariant('job-tracking', 'without_job_track');
        }
      } catch (error) {
        console.error(error);
      }
    };

    checkJobtrackAccess();
  }, [isHostInDomain, router]);

  if (isHostInDomain || !isProd) {
    return (
      <MarvelExperiment name="job-tracking">
        <MarvelVariant name="with_job_track" weight={6}>
          {children}
        </MarvelVariant>
        <MarvelVariant name="without_job_track" weight={4}>
          {children}
        </MarvelVariant>
      </MarvelExperiment>
    );
  }

  return <>{children}</>;
};

const Page = ({ children, host: propHost, refHost }) => {
  const router = useRouter();
  const [breakpoint, setBreakpoint] = useState();
  const [onlyMobile, setOnlyMobile] = useState(false);
  const [genMobileView, setGenMobileView] = useState(false);
  const [screenResolution, setScreenResolution] = useState({ width: undefined, height: undefined });
  const [deviceInfo, setDeviceInfo] = useState(null);

  const domain = useMemo(() => {
    const browserDomain = typeof window !== 'undefined' ? window.location.host : '';
    return propHost || browserDomain;
  }, [propHost]);

  const getInitialLocale = useMemo(() => {
    const { asPath = '', query = {} } = router || {};
    let path = asPath;
    if (path.includes('/builder')) {
      path = path.replace('/builder', '');
    }
    const pathLanguage = getLocaleFromPath(
      path,
      getDefaultLanguage(domain) === LANGUAGES_CODES.FRENCH,
      getDefaultLanguage(domain),
    );

    const availableLanguages = [
      ...getAvailableLanguages(propHost),
      ...(supported(
        ['bestonlineresume', 'resume-nation', 'elegantcv', 'modeles-cv', 'cvtoolspro', 'theresumeranger', 'resumedone'],
        propHost,
      ) || isJobTrackEnable()
        ? [LANGUAGES_CODES.FRENCH]
        : []),
    ];

    const defaultLanguage = availableLanguages.includes(pathLanguage) ? pathLanguage : getDefaultLanguage(domain);

    return query.language || defaultLanguage;
  }, [router, domain, propHost]);

  const handleResize = useCallback(
    throttle(() => {
      setBreakpoint(getCurrentBreakpoint());
      setOnlyMobile(getOnlyMobile());
      setGenMobileView(isGeneratorMobileView());
    }, 50),
    [],
  );

  useEffect(() => {
    if (!isProd) {
      customizePushtellDebugger();
    }

    const [width, height] = getScreenResolution();
    setBreakpoint(getCurrentBreakpoint());
    setScreenResolution({ width, height });
    setDeviceInfo(getDeviceInformation());
    setOnlyMobile(getOnlyMobile());
    setGenMobileView(isGeneratorMobileView());

    if (router.pathname === '/app') {
      marvelEmitter.setActiveVariant('job-tracking', 'with_job_track');
    }

    window.addEventListener('resize', handleResize);

    EXPERIMENT_LIST.forEach((exp) => removeExperiment(exp));
    localStorage.removeItem('redneg-hack');

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize, router.pathname]);

  const responsiveValue = useMemo(
    () => ({
      breakpoint,
      isMobile: ['sm', 'xs'].includes(breakpoint),
      isStrictMobile: onlyMobile === 'xs',
      onlyMobile,
      isGeneratorMobileView: genMobileView,
      isDev,
      isProd,
      isJobTrackDomain: isJobTrackEnable(),
      getDeviceInformation: () => deviceInfo,
      screenResolution,
      deviceInfo,
      host: domain,
      resumedoneBlogs: RESUMEDONE_BLOGS,
      refHost,
    }),
    [breakpoint, onlyMobile, genMobileView, deviceInfo, screenResolution, domain, refHost],
  );

  return (
    <MixpanelProvider>
      <ResponsiveProvider value={responsiveValue}>
        <QueryClientWrapper>
          <IntlProvider value={getInitialLocale}>
            <GlobalStateProvider>
              <PageRender host={domain} route={router.route}>
                <RenderJobTrack host={propHost}>{children}</RenderJobTrack>
              </PageRender>
            </GlobalStateProvider>
          </IntlProvider>
        </QueryClientWrapper>
      </ResponsiveProvider>
    </MixpanelProvider>
  );
};

Page.propTypes = {
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  host: PropTypes.string,
  refHost: PropTypes.string,
};

export default Page;
